import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { WifiOff, Wifi, RefreshCw } from "lucide-react";

/**
 * NetworkStatus — floating chip anchored bottom-center that surfaces the
 * browser's online/offline state.
 *
 * Behaviour:
 *  - When `navigator.onLine === false`, renders a persistent amber "You're
 *    offline" chip until connectivity is restored.
 *  - On transition back to online, shows a brief green "Back online" toast
 *    AND dispatches a `window.aura:online` custom event so pages doing
 *    polling / periodic refetch can immediately re-fetch stale data.
 *  - Offers a "Retry" affordance while offline that pings a tiny backend
 *    HEAD-ish endpoint (`/api/`) to force a re-check — useful because the
 *    browser's onLine flag is sometimes stale on captive-portal Wi-Fi.
 */
export function NetworkStatus() {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const wasOfflineRef = useRef(!online);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      if (wasOfflineRef.current) {
        toast.success("Back online", { icon: <Wifi className="h-4 w-4" /> });
        // Any page doing polling / refetch can subscribe to this event.
        window.dispatchEvent(new CustomEvent("aura:online"));
      }
      wasOfflineRef.current = false;
    };
    const handleOffline = () => {
      setOnline(false);
      wasOfflineRef.current = true;
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const retry = async () => {
    // Try a lightweight fetch to work around captive-portal / stale onLine.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      // Iter 98: use the versioned health endpoint. Both /api/ and
      // /api/v1/ are OK for a plain "am I online" check; keeping this
      // on /api/v1 makes drift observability accurate.
      const res = await fetch(`${backend}/api/v1/`, { cache: "no-store" });
      if (res.ok) {
        setOnline(true);
        toast.success("Back online", { icon: <Wifi className="h-4 w-4" /> });
        window.dispatchEvent(new CustomEvent("aura:online"));
        wasOfflineRef.current = false;
      } else {
        toast.error("Still offline");
      }
    } catch {
      toast.error("Still offline");
    }
  };

  if (online) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70]"
      data-testid="network-status-offline"
      role="status"
      aria-live="polite"
    >
      <div className="rounded-full bg-amber-500 text-white shadow-lg px-4 py-2.5 inline-flex items-center gap-3 border border-amber-600/40">
        <WifiOff className="h-4 w-4" />
        <span className="text-sm font-semibold">You&apos;re offline</span>
        <button
          onClick={retry}
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest bg-white/20 hover:bg-white/30 px-2 py-1 rounded-full transition-colors"
          data-testid="network-status-retry"
        >
          <RefreshCw className="h-3 w-3" /> Retry
        </button>
      </div>
    </div>
  );
}
